/**
 * FutureTripsPage · v2.7.65+E216 (driver)
 * ═════════════════════════════════════════════════════════════════════
 * The driver's "Future Trips" — advance bookings (from the Future Fares
 * system) ASSIGNED to this driver. A trip cannot be started until dispatch
 * RELEASES it. Once released, Start opens the driver's preferred GPS app to
 * the pickup and flips it to in-progress; Complete mints a real db.trips row
 * so it lands in Trip History AND admin Completed Rides with all original
 * booking data intact. Mirrors the Queued Rides lifecycle exactly.
 */
import { useCallback, useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2, MapPin, Phone, Play, Flag, Users, CalendarClock, RotateCcw, Lock, StickyNote,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtUsd } from "@/lib/format";
import { openNavigation } from "@/components/NavigateButton";
import TripNavButtons from "@/components/TripNavButtons";

const CT_TZ = "America/Chicago";
function fmtPickup(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      timeZone: CT_TZ, weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function FutureTripRow({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const pickup = row.pickup_address;
  const dropoff = row.dropoff_address;
  const released = !!row.released;
  const started = row.status === "in_progress";

  const start = async () => {
    // Fire the preferred-maps open synchronously on the click so mobile
    // popup-blockers don't eat it (never gate window.open behind an await).
    if (pickup) openNavigation(pickup);
    else toast.error("No pickup address on this trip");
    setBusy(true);
    try {
      await api.post(`/drivers/future-trips/${row.id}/start`);
      toast.success("Trip started · navigating to pickup");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trip");
    } finally { setBusy(false); }
  };

  const complete = async () => {
    if (!window.confirm("Mark this future trip as complete? It will move to Trip History.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/drivers/future-trips/${row.id}/complete`);
      toast.success(`Trip complete · ${fmtUsd(data?.trip?.total || 0)}`);
      if (data?.trip_id && window.confirm("Open the printable receipt?")) {
        window.open(`/receipt/${data.trip_id}`, "_blank");
      }
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className={`p-4 space-y-2 ${started ? "border-emerald-400/60" : released ? "border-violet-400/50" : "border-zinc-800"}`} data-testid={`future-trip-${row.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${
              started ? "bg-emerald-500/20 text-emerald-400 border-emerald-400/40"
                : released ? "bg-violet-500/20 text-violet-300 border-violet-400/40"
                : "bg-zinc-700/40 text-zinc-400 border-zinc-600"
            }`} data-testid={`future-trip-${row.id}-state`}>
              {started ? "IN PROGRESS" : released ? "RELEASED" : "AWAITING RELEASE"}
            </span>
            <span className="text-[10px] text-violet-300 font-mono flex items-center gap-1">
              <CalendarClock className="h-3 w-3"/>{fmtPickup(row.requested_pickup_at)}
            </span>
          </div>
          <div className="font-bold text-zinc-100 mt-1" data-testid={`future-trip-${row.id}-customer`}>{row.customer_name || "Rider"}</div>
          {row.rider?.phone && <div className="text-xs text-zinc-500 flex items-center gap-1"><Phone className="h-3 w-3"/>{row.rider.phone}</div>}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Fare</div>
          <div className="text-2xl font-black text-amber-500" data-testid={`future-trip-${row.id}-total`}>{fmtUsd(row.fare_total)}</div>
        </div>
      </div>

      <div className="space-y-1.5 text-sm">
        <div className="flex items-start gap-1.5" data-testid={`future-trip-${row.id}-pickup`}>
          <MapPin className="h-3.5 w-3.5 mt-0.5 text-emerald-400 flex-shrink-0"/>
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Pickup</div>
            <div className="text-zinc-200">{row.pickup_address || "—"}</div>
          </div>
        </div>
        <div className="flex items-start gap-1.5" data-testid={`future-trip-${row.id}-dropoff`}>
          <Flag className="h-3.5 w-3.5 mt-0.5 text-amber-400 flex-shrink-0"/>
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Drop-off</div>
            <div className="text-zinc-100">{row.dropoff_address || "—"}</div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 text-[10px] text-zinc-500 flex-wrap">
        <span className="flex items-center gap-1"><Users className="h-3 w-3"/>{row.passenger_count}</span>
        {row.payment_method && <span className="uppercase font-bold text-zinc-400">{row.payment_method}</span>}
      </div>

      {row.dispatcher_notes && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 flex items-start gap-2 text-xs" data-testid={`future-trip-${row.id}-notes`}>
          <StickyNote className="h-3.5 w-3.5 mt-0.5 text-amber-400 flex-shrink-0"/>
          <span className="text-zinc-200 italic">{row.dispatcher_notes}</span>
        </div>
      )}

      <div className="pt-1 border-t border-zinc-800 space-y-2">
        {!released ? (
          <div className="flex items-center justify-center gap-2 text-xs text-zinc-500 py-2" data-testid={`future-trip-${row.id}-locked`}>
            <Lock className="h-3.5 w-3.5"/>Waiting for dispatch to release this trip
          </div>
        ) : !started ? (
          <Button size="sm" onClick={start} disabled={busy} data-testid={`future-trip-${row.id}-start`} className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <Play className="h-3.5 w-3.5 mr-1"/>}
            Start Trip · Navigate to Pickup
          </Button>
        ) : (
          <>
            <TripNavButtons pickup={pickup} dropoff={dropoff} testidPrefix={`future-trip-${row.id}-nav`}/>
            <Button size="sm" onClick={complete} disabled={busy} data-testid={`future-trip-${row.id}-complete`} className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <Flag className="h-3.5 w-3.5 mr-1"/>}
              Complete
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}

export default function FutureTripsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/drivers/future-trips");
      setRows(data.future_trips || []);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Failed to load future trips");
    } finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    // v2.7.65+E223 · Tighter refresh so a dispatcher reassignment lands on the
    // driver's app within a few seconds (was 15s). Also refresh on focus AND
    // when the tab becomes visible again (returning from background).
    const onFocus = () => load(true);
    const onVisible = () => { if (document.visibilityState === "visible") load(true); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    const poll = setInterval(() => load(true), 5000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(poll);
    };
  }, [load]);

  return (
    <Layout title="Future Trips">
      <div className="max-w-2xl mx-auto space-y-3 pb-24">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-violet-400 font-bold">Advance Bookings</div>
            <h1 className="text-xl font-black">Future Trips</h1>
            <p className="text-xs text-zinc-500 mt-0.5">Scheduled rides assigned to you. Startable once dispatch releases them.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => load()} data-testid="future-trips-refresh"><RotateCcw className="h-3.5 w-3.5"/></Button>
        </div>
        {loading ? (
          <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin"/>Loading future trips…</div>
        ) : rows.length === 0 ? (
          <Card className="p-6 text-center text-sm text-zinc-500" data-testid="future-trips-empty">
            No future trips assigned to you yet. Advance bookings a dispatcher assigns to you will appear here.
          </Card>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => <FutureTripRow key={r.id} row={r} onChanged={() => load(true)}/>)}
          </div>
        )}
      </div>
    </Layout>
  );
}
